/**
 * Canvas-based JPEG re-encoding for web downloads.
 *
 * Stored photos are WebP, which many desktop photo tools and older apps handle
 * poorly. The web viewer's Save button re-encodes the fetched image to JPEG
 * before handing it to the browser, matching what the native viewer does with
 * expo-image-manipulator.
 */

// Fill behind the image: JPEG has no alpha, and an unfilled canvas would turn
// transparent pixels black.
const BACKGROUND_FILL = '#FFFFFF';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(new Error(`Failed to decode image: ${String(err)}`));
    img.src = src;
  });
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas toBlob returned null'));
        }
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Re-encode an image blob (any format the browser can decode) as a JPEG at its
 * natural size. Rejects if the image cannot be decoded or encoded.
 */
export async function convertImageBlobToJpeg(blob: Blob, quality: number): Promise<Blob> {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await loadImage(objectUrl);
    const width = image.naturalWidth;
    const height = image.naturalHeight;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas 2D context');
    }

    ctx.fillStyle = BACKGROUND_FILL;
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);

    return await canvasToJpeg(canvas, quality);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
